import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

/**
 * Generate a lightweight browser fingerprint from stable, non-PII signals.
 * The value is sent to the backend where it is HMAC-hashed with the server
 * secret before storage — the raw string never persists server-side.
 */
function generateDeviceFingerprint() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const parts = [
      navigator.userAgent,
      navigator.language || '',
      `${screen.width}x${screen.height}x${screen.colorDepth}`,
      tz,
      navigator.platform || '',
      String(navigator.hardwareConcurrency || ''),
      String(navigator.deviceMemory  || ''),
    ].join('||');
    // btoa gives us a compact, URL-safe representation of the combined string
    return btoa(unescape(encodeURIComponent(parts))).replace(/=/g, '').substring(0, 88);
  } catch {
    return '';
  }
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FreeTrialContext = createContext(null);

export const useFreeTrial = () => {
  const ctx = useContext(FreeTrialContext);
  if (!ctx) throw new Error('useFreeTrial must be used within FreeTrialProvider');
  return ctx;
};

export const FreeTrialProvider = ({ children }) => {
  // trial: { episode_id, last_position_seconds, completed, started_at, completed_at } | null
  const [freeTrial, setFreeTrial] = useState(null);
  const { accessToken, user, isAuthenticated } = useAuth();
  const fetchedRef = useRef(false);

  const isEligible = isAuthenticated && user && !user.is_pro &&
    user.role !== 'admin' && user.role !== 'creator';

  const fetchTrial = useCallback(async () => {
    if (!accessToken || !isEligible) return;
    try {
      const res = await axios.get(`${API}/me/free-trial`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setFreeTrial(res.data.trial);
    } catch {
      // ignore
    }
  }, [accessToken, isEligible]);

  // Fetch once per session when user becomes eligible
  useEffect(() => {
    if (isEligible && accessToken && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchTrial();
    }
    if (!isAuthenticated) {
      fetchedRef.current = false;
      setFreeTrial(null);
    }
  }, [isEligible, accessToken, isAuthenticated, fetchTrial]);

  /**
   * Start or resume the free trial for the given episodeId.
   * Returns { trial, media_url } on success, throws on error.
   */
  const startFreeTrial = useCallback(async (episodeId) => {
    const res = await axios.post(
      `${API}/me/free-trial/start`,
      {
        episode_id: episodeId,
        // Device fingerprint is sent once (on first claim). The backend
        // HMAC-hashes it with the server secret before storage.
        device_fingerprint: generateDeviceFingerprint(),
      },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    setFreeTrial(res.data.trial);
    return res.data; // { trial, media_url }
  }, [accessToken]);

  /**
   * Report playback position. Marks trial as completed when position >= 80% of duration.
   */
  const updateProgress = useCallback(async (episodeId, positionSeconds, durationSeconds) => {
    if (!freeTrial || freeTrial.episode_id !== episodeId) return;
    try {
      const res = await axios.post(
        `${API}/me/free-trial/progress`,
        { episode_id: episodeId, position_seconds: positionSeconds, duration_seconds: durationSeconds },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setFreeTrial(prev => prev ? {
        ...prev,
        last_position_seconds: positionSeconds,
        completed: res.data.completed,
      } : prev);
    } catch {
      // silently fail — progress updates are best-effort
    }
  }, [accessToken, freeTrial]);

  return (
    <FreeTrialContext.Provider value={{ freeTrial, isEligible, startFreeTrial, updateProgress, refreshTrial: fetchTrial }}>
      {children}
    </FreeTrialContext.Provider>
  );
};
